import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { Spinner } from "@/shared/components/spinner";
import { toastError } from "@/shared/lib/toast";

type ScreenState = "loading" | "invalid" | "confirm" | "declined";

interface DeclineInviteScreenProps<P> {
  /** The invite link's token; null/empty renders the invalid state. */
  token: string | null | undefined;
  /** Resolves the invite for the confirmation text; null means the link is dead. */
  loadPreview: (token: string) => Promise<P | null>;
  /** Performs the decline; resolves to an error message, or null on success. */
  decline: (token: string) => Promise<string | null>;
  confirmIcon: string;
  description: (preview: P) => ReactNode;
  declinedMessage: (preview: P | null) => ReactNode;
  invalidMessage: string;
  signInPath: string;
}

/**
 * Shared "Decline this invitation?" flow behind every invite email's decline
 * link (caregiver and clinician invites) — deliberately unauthenticated, so
 * someone can say no without signing up or logging in. Requires an explicit
 * click here (never declines on page load), so an email client's
 * link-prescanning can never trigger the decline by itself.
 */
export function DeclineInviteScreen<P>({
  token,
  loadPreview,
  decline,
  confirmIcon,
  description,
  declinedMessage,
  invalidMessage,
  signInPath,
}: DeclineInviteScreenProps<P>) {
  const [state, setState] = useState<ScreenState>("loading");
  const [preview, setPreview] = useState<P | null>(null);
  const [declining, setDeclining] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    let cancelled = false;
    loadPreview(token).then((data) => {
      if (cancelled) return;
      if (!data) {
        setState("invalid");
        return;
      }
      setPreview(data);
      setState("confirm");
    });
    return () => {
      cancelled = true;
    };
    // Keyed on the token only: callers pass inline wrappers around fixed API calls.
  }, [token]);

  const handleDecline = async () => {
    if (!token) return;
    setDeclining(true);
    const error = await decline(token);
    setDeclining(false);
    if (error) {
      toastError(error);
      return;
    }
    setState("declined");
  };

  if (state === "loading") {
    return (
      <div className="flex w-full max-w-sm items-center justify-center py-16">
        <Spinner size="large" />
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#F3F4F6]">
          <Icon icon="lucide:link-2-off" className="h-6 w-6 text-[#9CA3AF]" />
        </div>
        <h2 className="text-xl font-extrabold text-[#0A2947]">This link isn't valid</h2>
        <p className="!mt-2 text-sm text-[#9CA3AF]">{invalidMessage}</p>
        <Link to={signInPath} className="mt-6 inline-block text-sm font-semibold text-[#E4002B] hover:underline">
          Go to sign in
        </Link>
      </div>
    );
  }

  if (state === "declined") {
    return (
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#DCFCE7]">
          <Icon icon="lucide:check" className="h-6 w-6 text-[#16A34A]" />
        </div>
        <h2 className="text-xl font-extrabold text-[#0A2947]">Invitation declined</h2>
        <p className="!mt-2 text-sm text-[#9CA3AF]">{declinedMessage(preview)}</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF0F3]">
        <Icon icon={confirmIcon} className="h-6 w-6 text-[#E4002B]" />
      </div>
      <h2 className="text-center text-xl font-extrabold text-[#0A2947]">Decline this invitation?</h2>
      <p className="!mt-2 text-center text-sm text-[#9CA3AF]">{preview !== null && description(preview)}</p>

      <div className="mt-8 flex flex-col gap-3">
        <AuthSubmitButton htmlType="button" onClick={handleDecline} loading={declining}>
          Decline Invitation
        </AuthSubmitButton>
        <div className="flex justify-center">
          <PageBackButton onClick={() => window.history.back()} ariaLabel="Never mind, go back" />
        </div>
      </div>
    </div>
  );
}
